var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import { schema, normalize } from 'normalizr';
import { compose } from 'redux';
import addMissingIds from '../compat/addMissingIds';
import parseSelectorTarget from '../utility/parseTargetSelector';

function createEntity(name, processStrategy) {
  var options = {
    idAttribute: 'id'
  };
  if (processStrategy) {
    options.processStrategy = processStrategy;
  }

  return new schema.Entity(name, {}, options);
}

var collection = createEntity('collections'); // 1
var manifest = createEntity('manifests'); // 2
var canvas = createEntity('canvases'); // 3
var annotation = createEntity('annotations', function (original) {
  var body = Array.isArray(original.body) ? original.body : [original.body];
  var target = Array.isArray(original.target) ? original.target : [original.target];
  var newObj = _extends({}, original);
  if (original.body) {
    newObj.body = body;
  }
  if (original.target) {
    newObj.target = target;
  }
  return newObj;
}); // 4
var annotationPage = createEntity('annotationPages'); // 5
var annotationCollection = createEntity('annotationCollections'); // 6
var range = createEntity('ranges'); // 7
var contentResource = createEntity('contentResources'); // 8
var choice = createEntity('choices'); // 9
var canvasReference = createEntity('canvasReferences'); // 10
var selector = new schema.Entity('selectors', {
  id: canvas
}, {
  processStrategy: function processStrategy(value, parent, key) {
    // @todo This is an impossible path to hit. The process strategy is a strange function that
    // doesn't always hit the containing code. Will need to review at a later date.
    // if (typeof value === 'string') {
    //   return parseSelectorTarget(value);
    // }
    var id = value['@id'] || value.id;
    if (id) {
      return parseSelectorTarget(id);
    }
    return value;
  }
});

// Unofficial types.
var service = createEntity('services');

// Union types
var partOf = new schema.Array({
  collection: collection,
  manifest: manifest,
  contentResource: contentResource,
  annotationCollection: annotationCollection
}, function (entity) {
  switch (entity.type) {
    case 'Collection':
      return 'collection';
    case 'Manifest':
      return 'manifest';
    case 'AnnotationCollection':
      return 'annotationCollection';
    default:
      return 'contentResource';
  }
});

var canvasOrReference = new schema.Union({
  selector: selector,
  canvasReference: canvasReference,
  canvas: canvas
}, function (entity) {
  if (entity && entity.id && entity.id.indexOf('#') !== -1) {
    return 'selector';
  }
  if (entity.type === 'SpecificResource') {
    return 'canvasReference';
  }
  return 'canvas';
});

var annotationBodyMappings = {
  Choice: 'choice',
  Application: 'contentResource',
  Dataset: 'contentResource',
  Image: 'contentResource',
  Sound: 'contentResource',
  Text: 'contentResource',
  Video: 'contentResource',
  TextualBody: 'contentResource'
};
var annotationBody = new schema.Union({
  contentResource: contentResource,
  choice: choice
}, function (input) {
  if (annotationBodyMappings[input.type]) {
    return annotationBodyMappings[input.type];
  }
  return 'contentResource';
});

var rangeItem = new schema.Union({
  canvas: canvas,
  selector: selector,
  range: range,
  canvasReference: canvasReference
}, function (input) {
  if (input && input.id && input.id.indexOf('#') !== -1) {
    return 'selector';
  }

  switch (input.type) {
    case 'Canvas':
      return 'canvas';
    case 'Range':
      return 'range';
    case 'SpecificResource':
    default:
      // @todo default?
      return 'canvasReference';
  }
});

// Root resource
/**
 * Dereferencable resources:
 *
 * - Collection (Required)
 * - Manifest (Required)
 * - Canvas (Recommended)
 * - Annotation (Recommended)
 * - AnnotationPage (Required)
 * - AnnotationCollection (Optional)
 * - Content Resource (Required)
 * - Range (Optional)
 */
var RESOURCE_TYPE_MAP = {
  Collection: 'collection',
  Sequence: 'sequence',
  Manifest: 'manifest',
  Canvas: 'canvas',
  AnnotationCollection: 'annotationCollection',
  AnnotationPage: 'annotationPage',
  Annotation: 'annotation',
  Range: 'range',
  // Content resources.
  Application: 'contentResource',
  Dataset: 'contentResource',
  Image: 'contentResource',
  Sound: 'contentResource',
  Text: 'contentResource',
  Video: 'contentResource',
  TextualBody: 'contentResource'
};
var resource = new schema.Union({
  collection: collection,
  manifest: manifest,
  canvas: canvas,
  annotationCollection: annotationCollection,
  annotationPage: annotationPage,
  annotation: annotation,
  range: range,
  contentResource: contentResource,
  service: service
}, function (entity) {
  if (RESOURCE_TYPE_MAP[entity.type]) {
    return RESOURCE_TYPE_MAP[entity.type];
  }
  if (entity.profile) {
    return 'service';
  }
  throw Error('Unknown entity type');
});

// ===========================================================================
// 1) Collections
// ===========================================================================
collection.define({
  // Structural
  items: new schema.Array({
    collection: collection,
    manifest: manifest
  }, function (entity) {
    return entity.type === 'Manifest' ? 'manifest' : 'collection';
  }),
  annotations: [annotationPage],

  // Linking.
  seeAlso: [contentResource],
  service: [service],
  logo: [contentResource],
  homepage: contentResource,
  rendering: [contentResource],
  partOf: partOf,

  // Extra.
  thumbnail: [contentResource],
  posterCanvas: canvas
});

// ===========================================================================
// 2) Manifests
// ===========================================================================
manifest.define({
  items: [canvas],
  structures: [range],
  annotations: [annotationPage],

  // Linking
  seeAlso: [contentResource],
  service: [service],
  logo: [contentResource],
  homepage: contentResource,
  rendering: [contentResource],
  partOf: partOf,
  start: canvasOrReference,

  // Extra
  thumbnail: [contentResource],
  posterCanvas: canvas
});

// ===========================================================================
// 3) Canvases
// ===========================================================================
canvas.define({
  // Structural.
  items: [annotationPage],
  annotations: [annotationPage],

  // Linking
  seeAlso: [contentResource],
  service: [service],
  logo: [contentResource],
  homepage: contentResource,
  rendering: [contentResource],
  partOf: partOf,

  // Extra
  thumbnail: [contentResource],
  posterCanvas: canvas
});

// ===========================================================================
// 4) Annotation
// ===========================================================================
annotation.define({
  // Structural.
  body: [annotationBody],
  target: [selector],

  // Linking
  seeAlso: [contentResource],
  service: [service],
  logo: [contentResource],
  homepage: contentResource,
  rendering: [contentResource],
  partOf: partOf,

  // Extra
  thumbnail: [contentResource]
});

// ===========================================================================
// 5) Annotation Collection
// ===========================================================================
annotationCollection.define({
  // Structural.
  items: [annotationPage],

  // Linking
  seeAlso: [contentResource],
  service: [service],
  logo: [contentResource],
  homepage: contentResource,
  rendering: [contentResource],
  partOf: partOf,

  // extra
  thumbnail: [contentResource],

  // Paging.
  first: annotationPage,
  last: annotationPage
});

// ===========================================================================
// 6) Annotation Page
// ===========================================================================
annotationPage.define({
  // Structural.
  items: [annotation],

  // Linking
  seeAlso: [contentResource],
  service: [service],
  logo: [contentResource],
  homepage: contentResource,
  rendering: [contentResource],
  partOf: partOf,

  // Extra
  thumbnail: [contentResource],

  // Paging.
  next: annotationPage,
  previous: annotationPage
});

// ===========================================================================
// 7) Range
// ===========================================================================
range.define({
  // Structural.
  items: [rangeItem],
  annotations: [annotationPage],

  // Linking
  seeAlso: [contentResource],
  service: [service],
  logo: [contentResource],
  homepage: contentResource,
  rendering: [contentResource],
  partOf: partOf,
  start: canvasOrReference,
  supplementary: annotationCollection,

  // Extra
  thumbnail: [contentResource]
});

// ===========================================================================
// 8) Content Resource
// ===========================================================================
contentResource.define({
  // Linking.
  seeAlso: [contentResource],
  service: [service],
  logo: [contentResource],
  rendering: [contentResource],
  partOf: partOf,
  // Structural
  annotations: [annotationPage]
});

// ===========================================================================
// 9) Choice
// ===========================================================================
choice.define({
  default: annotation,
  item: [annotation]
});

// ===========================================================================
// 10) Canvas Reference
// ===========================================================================
canvasReference.define({
  source: canvas
});

var preprocess = compose(addMissingIds(3.0));

var normalizeResource = function normalizeResource(rawResource) {
  var customSchema = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : resource;
  return normalize(preprocess(rawResource), customSchema);
};

export { collection, manifest, canvas, annotation, annotationPage, annotationCollection, range, contentResource, choice, canvasReference, service, partOf, annotationBody, rangeItem, RESOURCE_TYPE_MAP, resource, preprocess, normalizeResource as normalize };